/**
 * 3Dセキュア エラーコード定数・ユーティリティ
 * WebとAppで共通使用
 *
 * 注意: API通信用の型定義（リクエスト/レスポンス）は
 * packages/openapi-specs で定義されており、自動生成されます。
 * - PostThreeDSecureEnrollRequestBody
 * - PostThreeDSecureEnrollResponseBody
 * - PostThreeDSecurePayRequestBody
 * - PostThreeDSecurePayResponseBody
 * など（UserAPIAgent.ts参照）
 */

// ============================================================================
// エラーコード定数
// ============================================================================

/**
 * 3DSエラーコード
 */
export const THREE_DS_ERROR_CODES = {
    /** 認証拒否 */
    REJECTED: "3DS001",
    /** タイムアウト */
    TIMEOUT: "3DS002",
    /** 認証失敗 */
    AUTH_FAILED: "3DS003",
    /** キャンセル */
    CANCELLED: "3DS004",
    /** セッション期限切れ */
    SESSION_EXPIRED: "3DS005",
    /** ブランド未対応 */
    BRAND_NOT_SUPPORTED: "3DS006",
    /** システムエラー */
    SYSTEM_ERROR: "3DS999"
} as const;

/**
 * エラーコードの型
 */
export type ThreeDSErrorCode = typeof THREE_DS_ERROR_CODES[keyof typeof THREE_DS_ERROR_CODES];

/**
 * 3DSエラーメッセージマップ
 */
export const THREE_DS_ERROR_MESSAGES: Record<ThreeDSErrorCode, string> = {
    [THREE_DS_ERROR_CODES.REJECTED]:
        "カード発行会社により認証が拒否されました。カード発行会社にお問い合わせください。",
    [THREE_DS_ERROR_CODES.TIMEOUT]:
        "時間内に認証が完了しませんでした。もう一度お試しください。",
    [THREE_DS_ERROR_CODES.AUTH_FAILED]:
        "認証に失敗しました。もう一度お試しください。",
    [THREE_DS_ERROR_CODES.CANCELLED]: "キャンセルされました。",
    [THREE_DS_ERROR_CODES.SESSION_EXPIRED]:
        "セッションの有効期限が切れました。もう一度お試しください。",
    [THREE_DS_ERROR_CODES.BRAND_NOT_SUPPORTED]:
        "このカードは3Dセキュアに対応していません。別のカードをご利用ください。",
    [THREE_DS_ERROR_CODES.SYSTEM_ERROR]:
        "システムエラーが発生しました。しばらく経ってからもう一度お試しください。"
};

/**
 * ZEUSエラーコードメッセージマップ（トークンAPI専用）
 * フロントエンドで使用するトークン化API固有のエラーコード
 * 参照: docs/design/20260106-3d-secure-implementation/zeus-error-code.md（トークン発行API）
 *
 * 注意: Enroll/Pay/termURL APIのエラーはサーバー側で処理されるため、ここには含まれません
 */
export const ZEUS_ERROR_MESSAGES: Record<string, string> = {
    // メンテナンスエラー
    "88888888":
        "メンテナンス中のため、現在ご利用いただけません。終了までしばらくお待ちください。",
    "90100100":
        "通信に失敗しました。電波状況の良い場所で再度お試しいただくか、しばらく時間をおいてください。",
    "99999999":
        "一時的なシステムエラーが発生しました。お手数ですが、最初からやり直してください。",

    // 通信・フォーマットエラー
    "02030105":
        "データの送信方法が正しくありません。ブラウザの「戻る」ボタンは使用せず、再度お手続きください。",
    "02030106":
        "通信エラーが発生しました。お手数ですが、最初からやり直してください。",
    "02030107": "送信データが空です。入力を確認して再度お試しください。",
    "02030108": "送信データが制限を超えています。入力内容をご確認ください。",
    "02030207": "入力データが正しく送信されませんでした。再度お試しください。",
    "02030208":
        "入力された文字数が多すぎます。内容を確認して短く入力してください。",
    "02030209":
        "送信データに不備があります。入力内容に特殊な記号が含まれていないかご確認ください。",
    "02080114":
        "手続きの処理内容が確認できませんでした。最初からやり直してください。",
    "02080115": "不正な操作が検出されました。正しい手順でお手続きください。",

    // 接続情報エラー
    "02130114":
        "接続元の確認ができませんでした。ネットワーク設定をご確認ください。",
    "02130117":
        "接続情報の形式が正しくありません。通信環境を変えて再度お試しください。",
    "02130110": "許可されていないネットワークからのアクセスです。",
    "02130118": "ご利用のネットワークからはお手続きいただけません。",

    // カード番号エラー
    "02130514": "カード番号が未入力です。半角数字で入力してください。",
    "02130517":
        "カード番号の桁数や形式が正しくありません。もう一度ご確認ください。",
    "02130619":
        "入力されたカード番号に誤りがあります。番号をよくお確かめください。",
    "02130620":
        "カード番号が正しくありません。有効なカード番号を入力してください。",
    "02130621": "入力されたカード番号はご利用いただけません。",
    "02130640":
        "カード番号に誤りがあります。お手元のカードを再度ご確認ください。",

    // 有効期限エラー
    "02130714": "有効期限（年）を選択または入力してください。",
    "02130717": "有効期限（年）の形式が正しくありません。",
    "02130725": "有効期限（年）が過ぎているか、入力に誤りがあります。",
    "02130814": "有効期限（月）を選択または入力してください。",
    "02130817": "有効期限（月）の形式が正しくありません。",
    "02130825": "有効期限（月）を正しい範囲（01〜12）で入力してください。",
    "02130922": "カードの有効期限が切れているか、入力内容が正しくありません。",

    // セキュリティコードエラー
    "02131014":
        "セキュリティコードが正しくありません。カード裏面（または表面）の番号をご確認ください。",
    "02131017":
        "セキュリティコードを正しい形式（3桁または4桁の半角数字）で入力してください。",

    // カード名義エラー
    "02131117": "カード名義は半角英大文字で入力してください。",
    "02131123": "カード名義に使用できない文字が含まれています。",
    "02131124": "カード名義を正しく入力してください（例: TARO YAMADA）。"
};

/**
 * リトライ可能なエラーコード（3DS系）
 */
const RETRYABLE_ERROR_CODES: ThreeDSErrorCode[] = [
    THREE_DS_ERROR_CODES.TIMEOUT,
    THREE_DS_ERROR_CODES.AUTH_FAILED,
    THREE_DS_ERROR_CODES.CANCELLED,
    THREE_DS_ERROR_CODES.SESSION_EXPIRED
];

/**
 * リトライ可能なZEUSエラーコード
 */
const RETRYABLE_ZEUS_ERROR_CODES: string[] = [
    "E00001", // 通信エラー
    "E01001", // カード番号エラー
    "E01002", // 有効期限エラー
    "E01003", // セキュリティコードエラー
    "G44", // セキュリティコード不一致
    "G56", // カード番号エラー
    "G68", // 金額エラー
    "003", // パラメータエラー
    "004" // 取引IDエラー
];

// ============================================================================
// エラーハンドリングユーティリティ
// ============================================================================

/**
 * エラーハンドリング結果
 */
export interface ThreeDSErrorHandlingResult {
    /** 表示用エラーメッセージ */
    displayMessage: string;
    /** リトライ可能フラグ */
    retryable: boolean;
}

/**
 * 3DSエラーをハンドリングして、表示メッセージとリトライ可否を返す
 * WebとAppの両方で使用する共通関数
 *
 * @param errorCode エラーコード（3DS系またはZEUS系）
 * @param errorMessage カスタムエラーメッセージ（オプション）
 * @returns エラーハンドリング結果
 */
export function handle3DSError(
    errorCode: string,
    errorMessage?: string
): ThreeDSErrorHandlingResult {
    // カスタムメッセージが指定されている場合は優先
    if (errorMessage && errorMessage.trim() !== "") {
        return {
            displayMessage: errorMessage,
            retryable: isRetryableError(errorCode as ThreeDSErrorCode)
        };
    }

    // 1. 3DSエラーコードを確認
    let message = THREE_DS_ERROR_MESSAGES[errorCode as ThreeDSErrorCode];

    // 2. ZEUSエラーコードを確認
    if (!message) {
        message = ZEUS_ERROR_MESSAGES[errorCode];
    }

    // 3. デフォルトメッセージ
    if (!message) {
        message = "エラーが発生しました。もう一度お試しください。";
    }

    return {
        displayMessage: message,
        retryable: isRetryableError(errorCode as ThreeDSErrorCode)
    };
}

/**
 * エラーコードがリトライ可能かどうかを判定
 *
 * @param errorCode エラーコード（3DS系またはZEUS系）
 * @returns リトライ可能な場合true
 */
function isRetryableError(errorCode: string): boolean {
    // 3DSエラーコードをチェック
    if (RETRYABLE_ERROR_CODES.includes(errorCode as ThreeDSErrorCode)) {
        return true;
    }

    // ZEUSエラーコードをチェック
    if (RETRYABLE_ZEUS_ERROR_CODES.includes(errorCode)) {
        return true;
    }

    return false;
}
